import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { playerCompetitionOptionsAtom, playerGamesAtom, playerSeasonOptionsAtom } from '@/atoms/queries/players';
import { FormattedGame, FormattedGameWithTeam } from '@/types/game';
import { getFilteredGames, getGamesBySeason, getSelectedGames } from '@/utils/atoms/game';
import {
  filterGamesByCompetition,
  filterGamesByTeam,
  filterGamesByGameType,
  pipeFilterFunctions,
  filterGamesBySeason,
} from '@/utils/filters';
import { loadable } from '@/utils/jotai';
import { convertToDropdownOption } from '@/utils/object';
import { filtersAtom } from '../filter';
import { competitionIdsToFilterByAtom } from '../highLevel/competitions';
import { seasonIdsToFilterByAtom } from '../highLevel/seasons';

const playerGameFilterFunctionsAtom = atom(get => {
  const selectedGameType = get(filtersAtom).gameType.value;
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);
  const selectedTeamIds = get(filtersAtom).teamIds;
  const selectedSeasonIds = get(seasonIdsToFilterByAtom);

  return pipeFilterFunctions<FormattedGame>(
    (entityGame: FormattedGameWithTeam[]) => filterGamesByGameType(entityGame, selectedGameType),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByCompetition(entityGame, selectedCompetitionIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesBySeason(entityGame, selectedSeasonIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByTeam(entityGame, selectedTeamIds),
  );
});

export const filteredPlayerGamesAtom = atom(async get => {
  const { startDate, endDate, gameRange, periodRange } = get(filtersAtom);
  return getFilteredGames(get, playerGamesAtom, playerGameFilterFunctionsAtom, {
    startDate,
    endDate,
    gameRange,
    periodRange,
  });
});

export const playerGamesBySeasonAtom = atom(get =>
  getGamesBySeason(get, playerSeasonOptionsAtom, playerCompetitionOptionsAtom, filteredPlayerGamesAtom),
);

const playerGamesOptionsAtom = atom(async get => {
  const playerGames = await get(filteredPlayerGamesAtom);
  return playerGames.map(({ game: { gameId, name } }) => convertToDropdownOption(name, gameId));
});

export const playerSelectedGamesAtom = atom(async get =>
  getSelectedGames(get, playerGamesOptionsAtom, get(filtersAtom).deselectedGameIds),
);

const playerSelectedGamesForQueryAtom = atom(async get => {
  const userPlayerSelectedGames = await get(playerSelectedGamesAtom);
  return userPlayerSelectedGames.length > 0 ? userPlayerSelectedGames : get(playerGamesOptionsAtom);
});

export const loadablePlayerSelectedGamesForQueryAtom = loadable(playerSelectedGamesForQueryAtom);

export const unwrappedPlayerSelectedGamesForQueryAtom = unwrap(playerSelectedGamesForQueryAtom, prev => prev || []);
